import { Card, CardContent, CardHeader, CardTitle } from "@aurumiq/ui/components/card";
import { StatusTag, statusVariant } from "@aurumiq/ui/composite/status-tag";
import { dmyTime } from "@aurumiq/ui/lib/format";
import { useState } from "react";
import { Link, useParams } from "react-router";
import { PageHeader } from "@/app/page-header";
import { Markdown } from "@/components/markdown";
import { QueryState, Stat, StatList } from "@/components/query-state";
import { ArtifactTable } from "@/features/releases/artifacts";
import { CiHelpCard } from "@/features/releases/ci-help-card";
import { useRelease } from "@/features/releases/queries";
import { ReleaseActions } from "@/features/releases/release-actions";
import { EditReleaseDialog } from "@/features/releases/release-dialogs";
import type { Release } from "@/lib/types";

export function ReleaseDetailPage() {
  const { version = "" } = useParams();
  const q = useRelease(version);
  const r = q.release;
  const [editing, setEditing] = useState<Release | null>(null);

  return (
    <>
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-2">
            <span className="font-mono">{version}</span>
            {r ? (
              <>
                <StatusTag variant={statusVariant(r.status)}>{r.status.toUpperCase()}</StatusTag>
                <StatusTag variant={statusVariant(r.channel)}>{r.channel.toUpperCase()}</StatusTag>
              </>
            ) : null}
          </span>
        }
        description={
          <Link to="/releases" className="text-primary hover:underline">
            ← All releases
          </Link>
        }
        actions={r ? <ReleaseActions release={r} onEdit={setEditing} variant="buttons" /> : null}
      />
      <QueryState query={q}>
        {() =>
          r ? (
            <div className="grid gap-4 lg:grid-cols-[1fr_20rem]">
              <div className="grid gap-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Artifacts</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ArtifactTable artifacts={r.artifacts} />
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle>Release notes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {r.notes_md.trim() ? (
                      <Markdown source={r.notes_md} />
                    ) : (
                      <p className="text-muted-foreground text-sm">No notes yet.</p>
                    )}
                  </CardContent>
                </Card>
              </div>
              <div className="grid content-start gap-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Details</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <StatList>
                      <Stat label="Channel">{r.channel}</Stat>
                      <Stat label="Staging">{r.staging_pct}%</Stat>
                      <Stat label="Created">{dmyTime(r.created_at)}</Stat>
                      <Stat label="Published">
                        {r.published_at ? dmyTime(r.published_at) : "—"}
                      </Stat>
                      <Stat label="Uploaded">
                        {r.artifacts.filter((a) => a.uploaded).length} / {r.artifacts.length}
                      </Stat>
                    </StatList>
                  </CardContent>
                </Card>
                <CiHelpCard version={r.version} channel={r.channel} />
              </div>
            </div>
          ) : (
            <Card>
              <CardContent className="text-muted-foreground text-sm">
                No release with version <span className="font-mono">{version}</span>.
              </CardContent>
            </Card>
          )
        }
      </QueryState>
      <EditReleaseDialog release={editing} onOpenChange={(o) => !o && setEditing(null)} />
    </>
  );
}
